// Nach der Ersteinrichtung: Musik hinein und hören, dass es läuft.

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { UploadZone } from "./LibraryPage";
import { Badge, LiveDot, Page } from "../components/ui";

/** Ein Schritt mit Nummer im Kreis; erledigte Schritte bekommen einen Haken. */
function Schritt({ nr, titel, fertig, children }: { nr: number; titel: string; fertig?: boolean; children: ReactNode }): JSX.Element {
  return (
    <section className="card relative p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${fertig ? "bg-emerald-500 text-white" : "bg-brand-primary/10 text-brand-primary ring-1 ring-inset ring-brand-primary/25"}`}>
          {fertig ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>
          ) : nr}
        </span>
        <div className="min-w-0 flex-1 space-y-3">
          <h2 className="pt-1.5 text-[0.95rem] font-semibold text-primary">{nr}. {titel}</h2>
          {children}
        </div>
      </div>
    </section>
  );
}

export function WelcomePage(): JSX.Element {
  const [music, setMusic] = useState(0);
  const [listenPage, setListenPage] = useState<string | null>(null);
  const [onAir, setOnAir] = useState(false);

  const load = useCallback(async () => {
    try {
      const o = await api.getOnboarding();
      setMusic(o.music_count);
      setListenPage(o.listen_page);
      setOnAir(Boolean(o.steps.find((s) => s.id === "on_air")?.done));
    } catch {
      // Ohne Antwort bleibt die Seite beim Hochladen stehen.
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <Page className="mx-auto max-w-3xl space-y-4">
      <header className="card brand-glow relative overflow-hidden p-6 sm:p-8">
        <Badge tone="brand">Ersteinrichtung</Badge>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight text-primary sm:text-3xl">Euer Radio ist angelegt 🎉</h1>
        <p className="mt-1.5 text-sm text-muted">Jetzt fehlt nur noch Musik. Sobald Titel da sind, sendet der Kanal von selbst.</p>
      </header>

      <Schritt nr={1} titel="Musik hochladen" fertig={music > 0}>
        <UploadZone onUploaded={() => void load()} />
        <p className="text-xs text-muted">
          {music === 0 ? "Noch keine Musik." : `${music} Titel da.`} Mit zehn und mehr Titeln wiederholt sich die Automatik nicht so schnell.
        </p>
      </Schritt>

      <Schritt nr={2} titel="Reinhören" fertig={onAir && Boolean(listenPage)}>
        {onAir && listenPage ? (
          <div className="callout callout-success items-center">
            <LiveDot />
            <span>Ihr seid auf Sendung. Eure Hörerseite: <a href={listenPage} target="_blank" rel="noreferrer" className="font-medium underline">{listenPage}</a></span>
          </div>
        ) : (
          <div className="well flex items-center gap-3 text-sm text-muted">
            <span className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-[color:var(--border-strong)] border-t-brand-primary" aria-hidden="true" />
            {music === 0 ? "Wartet auf die erste Musik …" : "Die Titel werden gerade gemessen; in wenigen Sekunden geht es los …"}
          </div>
        )}
      </Schritt>

      <Schritt nr={3} titel="Und dann">
        <ul className="space-y-2 text-sm text-secondary">
          <li><Link to="/servers" className="font-medium text-primary underline decoration-[color:var(--border-strong)] underline-offset-2 hover:decoration-brand-primary">Störungsmeldung einrichten</Link> – damit ihr erfahrt, wenn der Sender still ist.</li>
          <li><Link to="/studio" className="font-medium text-primary underline decoration-[color:var(--border-strong)] underline-offset-2 hover:decoration-brand-primary">Studio herunterladen und verbinden</Link> – für Live-Sendungen mit Mikrofon und Jingles.</li>
          <li><Link to="/channels/live/schedule" className="font-medium text-primary underline decoration-[color:var(--border-strong)] underline-offset-2 hover:decoration-brand-primary">Sendeplan</Link> und <Link to="/branding" className="font-medium text-primary underline decoration-[color:var(--border-strong)] underline-offset-2 hover:decoration-brand-primary">Aussehen der Hörerseite</Link> anpassen.</li>
        </ul>
        <Link to="/dashboard" className="btn btn-primary">Zum Dashboard</Link>
      </Schritt>
    </Page>
  );
}
